import { useState } from 'react';
import { useSelector } from 'react-redux';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonIcon from '@mui/icons-material/Person';
import { Box } from '@mui/material';

import { PqrRegisterHistory } from './registerHistory';
import { usePqrDetail } from './hook';
import { PqrAccountInfo } from './accountInfo';
import { PqrSmartHistory } from './smartHistory';
import { PqrDetailGeneralInfo } from './generalInfo';
import { PqrStatusHistory } from './statusHistory';
import { PqrAttachment } from './attachments';
import { PqrDataInfo } from './dataInfo';
import { PqrDataInitialCausality } from './initialCausality';
import { PqrNotificationClient } from './notificationClient';
import { PqrInfo } from './info';
import { PqrReconsideration } from './reconsideration';
import { PqrProductData } from './productData';
import { PqrStrategicAlly } from './strategicAlly';
import { PqrResponseMedium } from './responseMedium';
import { PqrObservationClose } from './closing/observationClose';
import { PqrComplianceTerms } from './closing/complianceTerms';
import { PqrNonComplianceReason } from './closing/nonComplianceReason';
import { PqrCompensation } from './closing/compensation';
import { PqrInFavorOf } from './inFavorOf';
import { PqrFinalCausality } from './protection';
import { PqrRedirect } from './redirect';
import { PqrDatesInfo } from './datesInfo';
import { PqrDataSfc } from './dataSfc';
import { PqrDetailProps } from './props';
import { PqrSurveyDetail } from './survey';
import { PqrSample } from './closing/sample';
import { PqrNotificationClose } from './closing/notificacionDfc';
import { isOnlyViewProfile } from '../../../../../shared/types/profiles';
import { PqrDataFinalCausality } from './finalCausality';
import { PqrHistorical } from '../historical';
import { PqrDetailActions } from './actions';
import { PqrAlertHistory } from './alertHistory';
import { PqrMailHistory } from './mailHistory';
import { PqrNotificationAreasHistory } from './notificationAreasHistory';

import { RootState } from '../../../../../redux/store';
import { LayoutModule } from '../../../../layout/modules';
import ActionButtonGroup from '../../../../components/general/buttons/actionButtonGroup';

import { ISelect } from '../../../../../shared/types/shared';
import {
    ACCOUNTS_DETAIL,
    PQR,
    PQR_EDIT,
    SAMPLES,
    SAMPLES_EDIT,
} from '../../../../../routes/paths';
import { PqrMailHistoryWithArea } from './mailHistoryWithArea';
// import { PqrSendToSFCIfAudit } from './closing/sendToSFCIfAudit';

export const PqrDetail = ({ samples }: PqrDetailProps) => {
    const [showHistorical, setShowHistorical] = useState(false);
    const { logUser } = useSelector((state: RootState) => state.general);
    const { type } = logUser || {};
    const { pqr, loading, pqrHistorical } = usePqrDetail(showHistorical);

    const showActionButtons = pqr && pqr?.id ? true : false;

    const toggleHistorical = () => {
        setShowHistorical(!showHistorical);
    };
    const additionalButtons =
        !isOnlyViewProfile(type) || type == 'calidad' ? (
            <>
                <PqrDetailActions pqr={pqr} samples={samples} />
                <ActionButtonGroup
                    id={pqr?.id || ''}
                    buttonSearch={{
                        show: true,
                        route: samples ? SAMPLES : PQR,
                    }}
                    buttonUpdate={{
                        show: !isOnlyViewProfile(type),
                        route: samples ? SAMPLES_EDIT : PQR_EDIT,
                    }}
                    buttonView={{
                        show: false,
                    }}
                    buttonLog={{
                        show: showActionButtons,
                    }}
                    onHistoricalShow={toggleHistorical}
                />
            </>
        ) : undefined;

    return (
        <>
            <LayoutModule
                title="Detalle caso PQR"
                withAccordion={false}
                titleChildren="Detalle cuenta"
                titleRigthChildren={
                    <a
                        href={`${ACCOUNTS_DETAIL}/${pqr?.account?.id}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <OpenInNewIcon sx={{ color: 'primary.main' }} />
                    </a>
                }
                icon={<PersonIcon sx={{ color: 'primary.main' }} />}
                additionalButtons={additionalButtons}
            >
                {showHistorical && (
                    <Box sx={{ marginTop: 2 }}>
                        <PqrHistorical
                            historical={pqrHistorical ?? []}
                            showHistorical={true}
                        />
                    </Box>
                )}
                <LayoutModule
                    title="Interno"
                    showTitle={false}
                    titleChildren="Detalle cuenta"
                    icon={<PersonIcon sx={{ color: 'primary.main' }} />}
                >
                    <PqrAccountInfo account={pqr?.account} loading={loading} />
                </LayoutModule>
            </LayoutModule>
            <PqrDetailGeneralInfo pqr={pqr} />
            <PqrRegisterHistory pqr={pqr} />
            <PqrDataInfo dataInfo={pqr?.data || undefined} />
            <PqrDataInitialCausality
                initialCausality={pqr?.initialCausality || undefined}
                initialCausalityCurrent={
                    typeof pqr?.initialCausalityCurrent !== 'string'
                        ? pqr?.initialCausalityCurrent
                        : undefined
                }
            />
            <PqrNotificationClient
                notificationClient={pqr?.notificationClients || undefined}
            />
            <PqrDataSfc
                status={pqr?.info?.status as ISelect}
                dataSfc={pqr?.dataSfc || undefined}
                hasReconsideration={pqr?.reconsideration?.hasReconsideration}
                gender={(pqr?.account?.gender as ISelect)?.name}
                isLGBTIQ={pqr?.account?.isLGBTIQ}
                half={(pqr?.data?.half as ISelect)?.name || undefined}
                dataReconsideration={pqr?.reconsideration || undefined}
            />
            <PqrInfo info={pqr?.info || undefined} />
            <PqrReconsideration
                reconsideration={pqr?.reconsideration || undefined}
            />
            <PqrProductData productData={pqr?.productData || undefined} />
            <PqrStrategicAlly strategicAlly={pqr?.strategicAlly || undefined} />
            <PqrDatesInfo
                datesInfo={pqr?.statusManagement || undefined}
                createdAt={pqr?.created_at}
            />
            <PqrRedirect redirect={pqr?.redirect || undefined} />
            <PqrInFavorOf inFavorOf={pqr?.inFavorOf || undefined} />
            <PqrDataFinalCausality
                finalCausality={pqr?.finalCausality || undefined}
            />
            <PqrFinalCausality
                finalCausality={pqr?.finalCausality || undefined}
            />
            <PqrResponseMedium
                responseMedium={pqr?.responseMedium || undefined}
            />
            <PqrObservationClose closing={pqr?.closing || undefined} />
            <PqrNotificationClose closing={pqr?.closing || undefined} />
            <PqrSample closing={pqr?.closing || undefined} />
            {/* TODO: reactivar "Enviar a la SFC" cuando se requiera en producción
            <PqrSendToSFCIfAudit closing={pqr?.closing || undefined} />
            */}
            <PqrComplianceTerms closing={pqr?.closing || undefined} />
            <PqrNonComplianceReason closing={pqr?.closing || undefined} />
            <PqrCompensation closing={pqr?.closing || undefined} />
            <PqrStatusHistory pqrId={pqr?.id || ''} />
            <PqrAlertHistory pqrId={pqr?.id || ''} />
            <PqrMailHistory pqrId={pqr?.id || ''} />
            <PqrMailHistoryWithArea pqrId={pqr?.id || ''} />
            <PqrNotificationAreasHistory pqrId={pqr?.id || ''} />
            <PqrAttachment attachment={pqr?.attachmentsFinally || []} />
            <PqrSmartHistory pqrId={pqr?.id || ''} />
            {pqr?.survey && (
                <PqrSurveyDetail
                    pqrSurvey={pqr?.survey}
                    halfName={(pqr.data?.half as ISelect).name}
                />
            )}
        </>
    );
};
